import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Target, Lightbulb, Heart, Rocket } from 'lucide-react';
import SEOHead from '../seo/SEOHead';
import Navbar from '../common/Navbar';
import Footer from '../common/Footer';
import './Pages.css';

const AboutPage: React.FC = () => {
  return (
    <>
      <SEOHead
        title="About Us - GymsSync | Best Gym Management Software India"
        description="Learn about GymsSync - our mission, vision, and commitment to revolutionizing gym management in India. Built for Indian gyms."
        keywords="about gymsync, about gymssync, gym management software company, fitness software India, gymsync company, gym management software about, gym software company India, fitness management company, gym ERP company, gym management software team, gym software developers, gym management solution provider"
        ogUrl={typeof window !== 'undefined' ? window.location.href : ''}
        canonicalUrl={typeof window !== 'undefined' ? `${window.location.origin}/about` : ''}
      />
      <Navbar />
      <div className="page-container">
        <div className="page-header">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="page-title"
          >
            About GymsSync
          </motion.h1>
        </div>

        <div className="page-content">
          <motion.section
            className="about-section-page"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="about-card">
              <Target size={48} className="about-icon" />
              <h2>Our Mission</h2>
              <p>
                At GymsSync, we're on a mission to revolutionize gym management in India. We understand 
                the challenges gym owners face daily—from managing hundreds of members to tracking 
                payments and maintaining attendance records. Our goal is to simplify these operations 
                so you can focus on what matters most: helping people achieve their fitness goals.
              </p>
            </div>

            <div className="about-card">
              <Lightbulb size={48} className="about-icon" />
              <h2>What We're Building</h2>
              <p>
                GymsSync is a comprehensive <strong>gym management software</strong> designed specifically 
                for the Indian market. We're creating a solution that combines powerful features with 
                affordability, making it accessible to gyms of all sizes—from small fitness studios to 
                large chains.
              </p>
              <p>
                Our platform integrates seamlessly with Indian payment gateways, supports local languages, 
                and understands the unique needs of Indian gym operations. We're not just building software; 
                we're building a community of successful gym owners.
              </p>
            </div>

            <div className="about-card">
              <Heart size={48} className="about-icon" />
              <h2>Why GymsSync?</h2>
              <ul className="about-list-page">
                <li>
                  <strong>Made for India:</strong> Built with Indian payment gateways, local language support, 
                  and deep understanding of Indian gym operations
                </li>
                <li>
                  <strong>Affordable:</strong> Pricing designed to be accessible for small and medium gyms 
                  without compromising on features
                </li>
                <li>
                  <strong>Easy to Use:</strong> Intuitive interface that requires no technical knowledge. 
                  Your staff can learn it in minutes
                </li>
                <li>
                  <strong>Comprehensive:</strong> All-in-one solution for member management, attendance, 
                  payments, and more—no need for multiple tools
                </li>
                <li>
                  <strong>Support:</strong> Dedicated customer support team to help you succeed. We're here 
                  when you need us
                </li>
                <li>
                  <strong>Scalable:</strong> Grows with your business. Start small and scale as you expand
                </li>
              </ul>
            </div>

            <div className="about-card">
              <Rocket size={48} className="about-icon" />
              <h2>Our Vision</h2>
              <p>
                We envision a future where every gym owner in India can focus on what they do best—helping 
                people achieve their fitness goals—while GymsSync handles the administrative work seamlessly 
                in the background.
              </p>
              <p>
                We believe that technology should empower, not complicate. That's why we're committed to 
                building tools that are powerful yet simple, comprehensive yet affordable, and innovative 
                yet reliable.
              </p>
              <p>
                Join us on this journey as we transform the fitness industry in India, one gym at a time.
              </p>
            </div>
          </motion.section>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AboutPage;

